import { projectAuthHeaders } from '../projectAuth';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiUpload } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa';
import { BACKEND_MAIN_URL } from '../config';
import FilePreview from './FilePreview';
import { useOfflineSession } from '../offline/OfflineSessionContext';
import { enqueueProjectFile, listQueuedProjectFiles, markQueuedFileStatus, removeQueuedProjectFile, subscribeToProjectFiles } from '../offline/projectFileQueue';
import { syncPendingProjectFiles } from '../offline/projectFileSync';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MIME_BY_EXTENSION = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  pdf: 'application/pdf', txt: 'text/plain', csv: 'text/csv',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};
const fileMime = (file) => {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  return MIME_BY_EXTENSION[file.name?.split('.').pop().toLowerCase()] || file.type;
};

// ── Categories — each one knows what mime types it accepts and how it’s
//    presented (icon, accent color, blurb under the title).
const CATEGORIES = [
  {
    id: 'document',
    icon: '📄',
    label: 'Documents',
    blurb: 'PDF, Excel, CSV, Word, TXT, or any image (map, scan, screenshot) — text and structure extracted in detail.',
    accent: '#0f766e',
    accept: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx', '.xls',
      'application/vnd.ms-excel',
      'text/csv', '.csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.docx',
      'text/plain', '.txt',
      'image/jpeg', 'image/png', 'image/webp',
    ],
    mimes: new Set([
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg', 'image/png', 'image/webp',
    ]),
  },
  {
    id: 'soil',
    icon: '🌍',
    label: 'Soil photos',
    blurb: 'Field or sample photos — analysed for texture, structure and signs.',
    accent: '#a16207',
    accept: ['image/jpeg', 'image/png', 'image/webp'],
    mimes: new Set(['image/jpeg', 'image/png', 'image/webp']),
  },
  {
    id: 'plant',
    icon: '🌿',
    label: 'Plant photos',
    blurb: 'Leaves, plants or rows — diagnosed for health and identification.',
    accent: '#15803d',
    accept: ['image/jpeg', 'image/png', 'image/webp'],
    mimes: new Set(['image/jpeg', 'image/png', 'image/webp']),
  },
  {
    id: 'lab',
    icon: '🧪',
    label: 'Lab analyses',
    blurb: 'Soil/water/foliar reports — values extracted and normalised.',
    accent: '#3730a3',
    accept: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
    mimes: new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']),
  },
];

const getFileId = (file) => file?.id || file?.file_id || file?._id || file?.document_id || null;

// The locale picks the units and Intl the decimal separator: 1229 bytes reads
// "1,2 Ko" in French and "1.2 KB" in English.
const formatBytes = (bytes, t, language) => {
  if (!bytes) return '';
  if (bytes < 1024) return t('fileSizeBytes', { size: new Intl.NumberFormat(language).format(bytes) });
  const oneDecimal = new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return t('fileSizeKilobytes', { size: oneDecimal.format(bytes / 1024) });
  return t('fileSizeMegabytes', { size: oneDecimal.format(bytes / (1024 * 1024)) });
};


const ProjectFiles = ({ projectId, files: initialFiles, onFilesChange, canEdit = true, serverCount = 0 }) => {
  const { t, i18n } = useTranslation('workspace');
  const { userId, offlineReadOnly, chatBackendUnavailable } = useOfflineSession();
  const account = userId || localStorage.getItem('userId');
  const [files, setFiles] = useState(initialFiles || []);
  const filesRef = useRef(initialFiles || []);
  const [pending, setPending] = useState([]);
  const [queueing, setQueueing] = useState(false);
  const [category, setCategory] = useState('document');
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);
  const selectedCategory = CATEGORIES.find((item) => item.id === category);
  const headers = () => (projectAuthHeaders());
  useEffect(() => { filesRef.current = initialFiles || []; setFiles(initialFiles || []); }, [initialFiles]);
  const updateFiles = useCallback((next) => { filesRef.current = next; setFiles(next); onFilesChange?.(next); }, [onFilesChange]);
  useEffect(() => {
    if (!account) return undefined;
    let active = true;
    const refresh = () => listQueuedProjectFiles(account, projectId)
      .then((rows) => { if (active) setPending(rows); })
      .catch(() => { if (active) setError('offlineFileStorageError'); });
    refresh();
    const unsubscribe = subscribeToProjectFiles((event) => {
      if (event.accountId !== account || (event.projectId && event.projectId !== projectId)) return;
      if (event.serverFile && !filesRef.current.some((file) => getFileId(file) === getFileId(event.serverFile))) {
        updateFiles([...filesRef.current, event.serverFile]);
      }
      refresh();
    });
    return () => { active = false; unsubscribe(); };
  }, [account, projectId, updateFiles]);
  useEffect(() => {
    if (!account || offlineReadOnly || chatBackendUnavailable) return undefined;
    const sync = () => { if (navigator.onLine !== false) syncPendingProjectFiles(account, { projectId }).catch(() => {}); };
    sync();
    window.addEventListener('online', sync);
    const visible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('online', sync); document.removeEventListener('visibilitychange', visible); };
  }, [account, projectId, offlineReadOnly, chatBackendUnavailable]);
  async function uploadFiles(list) {
    if (!canEdit || !account || queueing) return;
    setQueueing(true); setError('');
    for (const file of Array.from(list || [])) {
      const mime = fileMime(file);
      if (!selectedCategory.mimes.has(mime)) { setError(`${file.name}: ${t('fileType')}`); continue; }
      if (!file.size || file.size > MAX_FILE_SIZE) { setError(`${file.name}: ${t('fileSize')}`); continue; }
      try {
        const upload = file.type === mime ? file : new File([file], file.name, { type: mime });
        await enqueueProjectFile(account, projectId, upload, { category: selectedCategory.id,
          serverCount: Math.max(filesRef.current.length, serverCount) });
      } catch (failure) { setError(t(failure.message === 'File limit reached' ? 'fileLimit' : 'offlineFileStorageError')); break; }
    }
    setQueueing(false);
    if (!offlineReadOnly && !chatBackendUnavailable && navigator.onLine !== false) {
      syncPendingProjectFiles(account, { projectId }).catch(() => {});
    }
  }
  async function retryQueued(file) {
    try {
      await markQueuedFileStatus(account, projectId, file.id, 'pending');
      if (!offlineReadOnly && !chatBackendUnavailable && navigator.onLine !== false) syncPendingProjectFiles(account, { projectId }).catch(() => {});
    } catch { setError(t('offlineFileStorageError')); }
  }
  async function remove(file) {
    if (!canEdit) return;
    if (!window.confirm(t('removeFileConfirm', { name: file.name }))) return;
    const fileId = getFileId(file);
    if (!fileId) { setError(t('saveError')); return; }
    setDeleting(fileId); setError('');
    try {
      await axios.delete(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`, { headers: headers() });
      updateFiles(filesRef.current.filter((item) => getFileId(item) !== fileId));
    } catch { setError(t('saveError')); } finally { setDeleting(null); }
  }
  const full = Math.max(files.length, serverCount) + pending.length >= MAX_FILES;
  return <div className="ws-files">
    {canEdit && <div className={`ws-upload${dragging ? ' ws-upload-dragging' : ''}`} onDragOver={(event) => { event.preventDefault(); if (!full) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); if (!full) uploadFiles(event.dataTransfer.files); }}>
      <label className="ws-field">{t('fileCategory')}<select value={category} onChange={(event) => setCategory(event.target.value)}>{CATEGORIES.map((item) => <option key={item.id} value={item.id}>{t(item.id)}</option>)}</select></label>
      <button className="ws-primary" type="button" disabled={full || queueing} onClick={() => inputRef.current?.click()}><FiUpload />{t('addFiles')}</button>
      <input type="file" hidden ref={inputRef} accept={selectedCategory.accept.join(',')} multiple onChange={(event) => { uploadFiles(event.target.files); event.target.value = ''; }} />
      <p>{t(category === 'document' ? 'fileFormats' : category === 'lab' ? 'labFormats' : 'imageFormats')}</p>
      <p>{t('offlineFilesHint')}</p>
    </div>}
    {error && <p className="ws-error" role="alert">{t(error)}</p>}
    <p className="ws-file-count">{Math.max(files.length, serverCount) + pending.length}/{MAX_FILES} {t('documents').toLowerCase()}</p>
    {!files.length && !pending.length && <p className="ws-muted">{t('noFiles')}</p>}
    <ul className="ws-file-list">{[...files, ...pending].map((file) => {
      const queued = Boolean(file.blob);
      const uploading = queued && file.status === 'uploading';
      return <li key={getFileId(file) || file.tempId}>
        <FilePreview compact file={{ ...file, localFile: file.blob || file.localFile }} />
        <div><strong title={file.name}>{file.name}</strong><small role={file.status === 'failed' ? 'alert' : undefined}>{uploading ? t('uploading') : file.status === 'failed' ? t('offlineUploadRejected') : queued ? t('offlineUploadPending') : `${t(file.category || 'document')} · ${formatBytes(file.size, t, i18n.resolvedLanguage)}`}</small>{queued && file.status === 'failed' && <button className="ws-text-button" type="button" onClick={() => retryQueued(file)}>{t('retry')}</button>}</div>
        {canEdit && <button className="ws-icon-button" type="button" disabled={uploading || deleting === getFileId(file) || (!queued && (offlineReadOnly || chatBackendUnavailable || navigator.onLine === false))} aria-label={t('remove', { name: file.name })} onClick={() => queued ? removeQueuedProjectFile(account, projectId, file.id).catch(() => setError(t('offlineFileStorageError'))) : remove(file)}><FaTrash /></button>}
      </li>;
    })}</ul>
  </div>;
};
export default ProjectFiles;
